import { useDispatch, useSelector } from "react-redux";
import { PlayIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion } from "framer-motion";
import { setSidebarSectionExpanded } from "redux/slices/navigation";
import { SidebarLink, useSidebar } from "components/ui/sidebar";

export const CollapsibleSidebarOptions = ({
    label = 'Sales Tools',
    links = [],
    isTouchDevice = false,
}) => {
    const dispatch = useDispatch();
    
    const { open, animate } = useSidebar();
    const expanded = useSelector((state) => state.navigation.expandedSidebarSections?.[label] ?? false);

    const labelsVisible = isTouchDevice || !animate || open;

    const toggleExpanded = () => {
        dispatch(setSidebarSectionExpanded({
            section: label,
            expanded: !expanded,
        }));
    };

    return (
        <div>
            <button
                type="button"
                aria-expanded={expanded}
                aria-label={label}
                onClick={toggleExpanded}
                className={`group/sidebar flex w-full cursor-pointer items-center bg-transparent text-left ${isTouchDevice ? "flex-col justify-center gap-0.5 py-2" : "justify-start gap-2 py-2"}`}
            >
                <motion.div
                    className={`flex flex-shrink-0 items-center justify-center ${isTouchDevice ? "h-8 w-8" : "ml-1 h-5 w-5"}`}
                    initial={false}
                    animate={{ rotate: expanded ? 90 : 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                    <PlayIcon className={`text-neutral-500 transition-colors duration-150 group-hover/sidebar:text-white ${isTouchDevice ? "h-5 w-5" : "h-4 w-4"}`} />
                </motion.div>

                {isTouchDevice ? (
                    <p className="text-center text-xxs text-neutral-400 leading-tight transition-colors duration-150 group-hover/sidebar:text-white">
                        {label}
                    </p>
                ) : (
                    <motion.span
                        initial={{
                            display: animate ? (open ? "inline-block" : "none") : "inline-block",
                            opacity: animate ? (open ? 1 : 0) : 1,
                        }}
                        animate={{
                            display: animate ? (open ? "inline-block" : "none") : "inline-block",
                            opacity: animate ? (open ? 1 : 0) : 1,
                        }}
                        className="text-neutral-500 font-bold text-sm group-hover/sidebar:translate-x-1 group-hover/sidebar:text-white transition duration-150 whitespace-pre inline-block !p-0 !m-0"
                    >
                        {label}
                    </motion.span>
                )}
            </button>

            <AnimatePresence initial={false}>
                {expanded && links.length > 0 && (
                    <motion.div
                        key="links"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                            height: labelsVisible ? "auto" : 0,
                            opacity: labelsVisible ? 1 : 0,
                        }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        {links.map((link, index) => (
                            <SidebarLink
                                key={link.href ?? index}
                                link={link}
                                isTouchDevice={isTouchDevice}
                                nested
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
